import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostBinding,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
} from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { UiTranslateModule } from '@fcom/ui-translate/ui-translate.module';

import { ButtonSize, ButtonTheme } from '../../buttons/button/enums';
import { IconButtonSize, IconButtonTheme } from '../../buttons/icon-button/enums';
import { IconSize, type IconName, type IconSet } from '../../icons/icons.interfaces';
import { LoaderTheme } from '../../loader/enums';
import { LinkSize } from '../../link/enums';
import { NotificationButtonStyle, NotificationLayout, NotificationScale, NotificationTheme } from '../enums';
import { NotificationI18n } from '../interfaces';
import { IconComponent } from '../../icons/icon/icon.component';
import { LinkComponent } from '../../link/link.component';
import { LoaderComponent } from '../../loader/loader.component';
import { IconButtonComponent } from '../../buttons/icon-button/icon-button.component';
import { ButtonComponent } from '../../buttons/button/button.component';

interface ScaleConfig {
  iconSize: IconSize;
  linkSize: LinkSize;
  font: {
    body: string;
    header?: string;
  };
}

interface NewBrandSignalClasses {
  border: string;
  icon: string;
}

/**
 * A component to display varied forms of notifications.
 *
 * Zeplin designs are available [here](https://zpl.io/adG00Wl)
 *
 * There is many options available, but in many cases you won't need most of these.
 *
 * @example
 * <fcom-notification
 *   [theme]="theme"
 *   [layout]="layout"
 *   [isClosable]="isClosable"
 *   [linkText]="linkText"
 *   [linkUrl]="linkUrl"
 *   [iconName]="SvgLibraryIcon.INFO_BLOCK"
 *   [contentSpaceClass]="ContentSpaceClass.PX_SMALL_Y"
 *   (linkClick)="linkClick($event)"
 *   (closeClick)="closeClick($event)">
 *   {{text}}
 * </fcom-notification>
 */
@Component({
  selector: 'fcom-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgTemplateOutlet,
    IconComponent,
    LinkComponent,
    LoaderComponent,
    IconButtonComponent,
    ButtonComponent,
    UiTranslateModule,
  ],
})
export class NotificationComponent {
  readonly NotificationButtonStyle = NotificationButtonStyle;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;

  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;
  readonly LoaderTheme = LoaderTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly LinkSize = LinkSize;
  readonly NotificationScale = NotificationScale;

  @HostBinding('class') hostClass = 'block';

  /**
   * What color scheme to use for the component
   */
  theme: InputSignal<NotificationTheme> = input<NotificationTheme>(NotificationTheme.INFO);

  /**
   * Whether the component should be block, full width or toast
   */
  layout: InputSignal<NotificationLayout> = input<NotificationLayout>(NotificationLayout.BLOCK);

  /**
   * The scale of the component, which affects the size of the icon and text
   */
  scale: InputSignal<NotificationScale> = input<NotificationScale>(NotificationScale.MEDIUM);

  /**
   * Determines the style of buttons in the notification component,
   * allowing links to be shown instead of traditional buttons.
   */
  buttonStyle: InputSignal<NotificationButtonStyle> = input<NotificationButtonStyle>();

  /**
   * Override the close icon
   */
  closeIcon: InputSignal<IconSet> = input<IconSet>({
    category: IconLibrary.SVG_LIBRARY,
    name: SvgLibraryIcon.CLOSE_DELETE,
  });

  /**
   * Displays a loading animation.
   */
  isLoading: InputSignal<boolean> = input<boolean>(false);

  /**
   * Whether to show the close icon or not
   */
  isClosable: InputSignal<boolean> = input<boolean>(false);

  title: InputSignal<string> = input<string>();

  /**
   * Whether to display link text wrapped (on the same line) or not (on a new line).
   */
  horizontal: InputSignal<boolean> = input<boolean>(false);

  /**
   * In case of EDGE_TO_EDGE layout is used without the need of wrapping the content in columns,
   * then this flag should be set to true
   */
  excludeColumns: InputSignal<boolean> = input<boolean>(false);

  /**
   * The text to use for a link. If no URL is passed it will be rendered as a button. Otherwise, it
   * will be an A tag.
   */
  linkText: InputSignal<string> = input<string>();

  /**
   * The url to go to. If you wish to handle this behavior yourself you can listen to the linkClick
   * event and `event.preventDefault` to stop the browser changing the page.
   */
  linkUrl: InputSignal<string> = input<string>();

  /**
   * Whether the link should open in a new window or not. Only applicable when linkText + linkUrl
   * is provided.
   */
  linkOpenInNewWindow: InputSignal<boolean> = input<boolean>(false);

  /**
   * The text to use for a secondary link. If no URL is passed it will be rendered as a button. Otherwise, it
   * will be an A tag.
   */
  secondaryLinkText: InputSignal<string> = input<string>();

  /**
   * The url to go with secondary link. If you wish to handle this behavior yourself you can listen to the linkClick
   * event and `event.preventDefault` to stop the browser changing the page.
   */
  secondaryLinkUrl: InputSignal<string> = input<string>();

  /**
   * Whether the link should open in a new window or not. Only applicable when linkText + linkUrl
   * is provided.
   */
  secondaryLinkOpenInNewWindow: InputSignal<boolean> = input<boolean>(false);

  /**
   * An icon to override the default icon used. Note that each NotificationTheme has a different
   * default icon
   */
  iconName: InputSignal<IconName> = input<IconName>();

  iconCategory: InputSignal<IconLibrary> = input<IconLibrary>(IconLibrary.SVG_LIBRARY);

  iconSize: InputSignal<IconSize> = input<IconSize>();

  /**
   * Any i18n labels used by the component. In most cases you can use the default for the component
   */
  i18n: InputSignal<NotificationI18n> = input<NotificationI18n>({
    closeAriaLabel: 'actions.close',
  });

  removeHorizontalPadding: InputSignal<boolean> = input<boolean>(false);

  /**
   * When the button/a tag is clicked the parent component can listen and handle the event. Note
   * that the event is sent so that you can stopPropagation/preventDefault if needed.
   */
  linkClick: OutputEmitterRef<Event> = output<Event>();

  /**
   * When the secondary button/a tag is clicked the parent component can listen and handle the event. Note
   * that the event is sent so that you can stopPropagation/preventDefault if needed.
   */
  secondaryLinkClick: OutputEmitterRef<Event> = output<Event>();

  /**
   * The close button was clicked and the notification should be closed. The parent component
   * should either remove the notification from the dom or hide it in some way.
   */
  closeClick: OutputEmitterRef<Event> = output<Event>();

  displayedIcon = computed<IconName>(() => this.iconName() ?? this.getDefaultIconForTheme());

  readonly sizes = computed(() => this.SCALE_CONFIG[this.scale()]);

  readonly computedTheme = computed((): NotificationTheme => {
    switch (this.theme()) {
      case NotificationTheme.SUSTAINABILITY:
        return NotificationTheme.INFO;
      case NotificationTheme.ALERT:
      case NotificationTheme.DISRUPTION:
        return NotificationTheme.ERROR;
      case NotificationTheme.CAMPAIGN:
        return NotificationTheme.PROMOTION;
      default:
        return this.theme();
    }
  });

  private readonly signalClasses = computed(() => this.NEW_BRAND_CLASSES[this.computedTheme()]);

  readonly notificationStyle = computed((): Record<string, boolean> => {
    const isBlockOrOverlay = [NotificationLayout.BLOCK, NotificationLayout.OVERLAY].includes(this.layout());
    const isOverlay = this.layout() === NotificationLayout.OVERLAY;
    const theme = this.theme();

    const classes: Record<string, boolean> = {
      'nordic-blue-50-bg': theme === NotificationTheme.INFO,
      'success-50-bg': theme === NotificationTheme.SUCCESS,
      'alert-50-bg': theme === NotificationTheme.WARNING,
      'error-50-bg': [NotificationTheme.ALERT, NotificationTheme.DISRUPTION, NotificationTheme.ERROR].includes(theme),
      'birch-50-bg': theme === NotificationTheme.SUSTAINABILITY,
      'heather-700-bg': theme === NotificationTheme.CAMPAIGN,
      'signal-information-notification-bg': [NotificationTheme.INFO, NotificationTheme.SUSTAINABILITY].includes(theme),
      'signal-success-notification-bg': theme === NotificationTheme.SUCCESS,
      'signal-warning-notification-bg': theme === NotificationTheme.WARNING,
      'signal-error-notification-bg': [
        NotificationTheme.ALERT,
        NotificationTheme.DISRUPTION,
        NotificationTheme.ERROR,
      ].includes(theme),
      'signal-promotion-notification-bg': [NotificationTheme.CAMPAIGN, NotificationTheme.PROMOTION].includes(theme),
      'nordic-blue-900-text': [
        NotificationTheme.INFO,
        NotificationTheme.SUCCESS,
        NotificationTheme.WARNING,
        NotificationTheme.ALERT,
        NotificationTheme.ERROR,
        NotificationTheme.SUSTAINABILITY,
      ].includes(theme),
      'error-900-text': theme === NotificationTheme.DISRUPTION,
      'white-text text-icon-static-light': theme === NotificationTheme.CAMPAIGN,
      'text-icon-primary': [
        NotificationTheme.INFO,
        NotificationTheme.SUCCESS,
        NotificationTheme.WARNING,
        NotificationTheme.SUSTAINABILITY,
        NotificationTheme.ALERT,
        NotificationTheme.DISRUPTION,
        NotificationTheme.ERROR,
      ].includes(theme),
      'text-icon-reversed': [NotificationTheme.CAMPAIGN, NotificationTheme.PROMOTION].includes(theme),
      'rounded-large': isBlockOrOverlay,
      'elevation-level-1': isOverlay,
    };

    const border = this.signalClasses()?.border;
    if (border) {
      classes[border] = true;
      classes['new-brand-bordered'] = true;
    }
    return classes;
  });

  readonly iconColorClass = computed((): string => {
    if (this.theme() === NotificationTheme.CAMPAIGN) {
      return 'text-icon-static-light';
    }
    return this.signalClasses()?.icon ?? '';
  });

  readonly textContainerStyle = computed(() => [
    ...(this.horizontal() ? ['flex--wrap'] : ['flex--column', 'row-gap']),
    ...(this.sizes().font?.body ? [this.sizes().font?.body] : []),
    ...(this.scale() === NotificationScale.LARGE ? (this.title() ? ['ms-xxxsmall-t'] : ['ms-xxsmall-t']) : []),
  ]);

  readonly wrapperStyle = computed(() =>
    [
      this.scale() !== NotificationScale.SMALL && !this.removeHorizontalPadding() ? 'ps-medium' : '',
      this.scale() !== NotificationScale.SMALL && this.removeHorizontalPadding() ? 'ps-medium-y' : '',
      this.scale() === NotificationScale.SMALL && !this.removeHorizontalPadding() ? 'ps-xsmall' : '',
      this.scale() === NotificationScale.SMALL && this.removeHorizontalPadding() ? 'ps-xsmall-y' : '',
      this.scale() === NotificationScale.SMALL ? 'flex--middle' : '',
    ].filter(Boolean)
  );

  private readonly NEW_BRAND_CLASSES: Partial<Record<NotificationTheme, NewBrandSignalClasses>> = {
    [NotificationTheme.INFO]: {
      border: 'signal-information-notification-border-icon-border',
      icon: 'signal-information-notification-border-icon-text',
    },
    [NotificationTheme.SUCCESS]: {
      border: 'signal-success-notification-border-icon-border',
      icon: 'signal-success-notification-border-icon-text',
    },
    [NotificationTheme.WARNING]: {
      border: 'signal-warning-notification-border-icon-border',
      icon: 'signal-warning-notification-border-icon-text',
    },
    [NotificationTheme.ERROR]: {
      border: 'signal-error-notification-border-icon-border',
      icon: 'signal-error-notification-border-icon-text',
    },
    [NotificationTheme.TRANSPARENT]: {
      border: 'notification-transparent-border',
      icon: '',
    },
    [NotificationTheme.PROMOTION]: {
      border: '',
      icon: 'text-icon-reversed',
    },
  };

  private SCALE_CONFIG: Record<NotificationScale, ScaleConfig> = {
    [NotificationScale.SMALL]: {
      iconSize: IconSize.SMALL,
      linkSize: LinkSize.SMALL,
      font: {
        body: 'font-body-small',
      },
    },
    [NotificationScale.MEDIUM]: {
      iconSize: IconSize.MEDIUM,
      linkSize: LinkSize.MEDIUM,
      font: {
        body: 'font-body-medium',
      },
    },
    [NotificationScale.LARGE]: {
      iconSize: IconSize.LARGE,
      linkSize: LinkSize.MEDIUM,
      font: {
        body: 'font-body-medium',
        header: 'font-title-small',
      },
    },
  };

  private getDefaultIconForTheme(): SvgLibraryIcon {
    switch (this.theme()) {
      case NotificationTheme.SUCCESS:
        return SvgLibraryIcon.CHECKMARK_BLOCK;
      case NotificationTheme.WARNING:
      case NotificationTheme.ALERT:
      case NotificationTheme.ERROR:
        return SvgLibraryIcon.ALERT_TRIANGLE;
      case NotificationTheme.SUSTAINABILITY:
        return SvgLibraryIcon.SUSTAINABILITY;
      case NotificationTheme.CAMPAIGN:
      case NotificationTheme.PROMOTION:
        return SvgLibraryIcon.DISCOUNT_BLOCK;
      case NotificationTheme.DISRUPTION:
        return SvgLibraryIcon.ALERT_BLOCK;
      case NotificationTheme.INFO:
      case NotificationTheme.TRANSPARENT:
      default:
        return SvgLibraryIcon.INFO_BLOCK;
    }
  }
}
